import { Box, NavList, Paragraph } from '@deckee/deck-hand';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import styled from 'styled-components';
import captureSpecificError from '../../../../services/capture-error';
import { getDeviceId } from '../../../../services/event-capture/anonymous-data-capture';
import {
  ROUTE_ACCOUNT_SETTINGS_INTERESTS,
  ROUTE_MAP,
  ROUTE_MY_BOATS,
  ROUTE_SETTINGS,
  ROUTE_SETTINGS_LOCATION,
} from '../../../../services/route/route-names';
import { transitionToNextRoute } from '../../../../services/route/route-navigation';
import {
  boat,
  cross,
  handWave,
  location,
  ReactSvg,
  settings,
} from '../../../shared/vector-images';
import { captureEvent } from '../../../../services/event-capture';
import usePaywall from '../../../hooks/use-paywall';
import { selectSubscriptionPortalUrl } from '../../../../services/store/modules/subscriptions';
import BasicDrawerLayout from '../../../layouts/BasicDrawerLayout/BasicDrawerLayout';
import { LayoutBody } from '../../../layouts/components/LayoutBody/LayoutBody';

const DeviceIdText = styled(Box)`
  word-break: break-all;
`;

const AccountSettingsPage = () => {
  const history = useHistory();
  const { presentSubscriptionPortal, hasSubscriptionFeaturesEnabled } =
    usePaywall('Account settings');

  const portalUrl = useSelector(selectSubscriptionPortalUrl);

  const [deviceID, setDeviceId] = useState<string | undefined>();

  const [textCopied, setTextCopied] = useState(false);

  const [shaCopied, setShaCopied] = useState(false);

  const unsubscribeHandler = async (url: string) => {
    await captureEvent({
      type: 'profile: Manage subscription',
      payload: {
        action: 'link clicked',
      },
      sendToAmplitude: true,
    });
    presentSubscriptionPortal(url);
  };

  useEffect(() => {
    const fetchDeviceId = async () => {
      const dId = await getDeviceId();
      setDeviceId(dId);
    };

    fetchDeviceId();
  }, []);

  const GITHUB_SHA = window?.DECKEE_CONFIG?.GITHUB_SHA;

  const copyTextToClipboard = async ({
    text,
    setCopied,
  }: {
    text: string;
    setCopied: (value: boolean) => void;
  }) => {
    try {
      // try to use the navigator.clipboard api
      // fallback to input field copy which only works with readonly on the input field
      const el = document.createElement('textarea');
      el.value = text;
      el.setAttribute('readonly', '');
      el.style.position = 'absolute';
      el.style.left = '-9999px';
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopied(true);
    } catch (error) {
      captureSpecificError(
        error,
        'containers/app-main-client/src/components/features/AccountSettings/AccountSettingsPage/AccountSettingsPage.tsx',
      );
    }
  };

  return (
    <BasicDrawerLayout
      heading="Account settings"
      onBack={() =>
        transitionToNextRoute(ROUTE_SETTINGS, {
          /* parameters */
        })
      }
      onClose={() => transitionToNextRoute(ROUTE_MAP)}
    >
      <LayoutBody>
        <NavList>
          <NavList.Item
            chevron
            onClick={() =>
              transitionToNextRoute(ROUTE_ACCOUNT_SETTINGS_INTERESTS)
            }
          >
            <ReactSvg
              toDisplay={handWave()}
              options={{
                fillColor: '#BABABA',
              }}
              width={32}
              height={32}
            />
            <Paragraph>My interests</Paragraph>
          </NavList.Item>
          <NavList.Item
            chevron
            onClick={() => transitionToNextRoute(ROUTE_MY_BOATS)}
          >
            <ReactSvg
              toDisplay={boat()}
              options={{
                fillColor: '#BABABA',
              }}
              width={32}
              height={32}
            />
            <Paragraph>My watercrafts</Paragraph>
          </NavList.Item>
          <NavList.Item
            chevron
            onClick={() => transitionToNextRoute(ROUTE_SETTINGS_LOCATION)}
          >
            <ReactSvg
              toDisplay={location()}
              options={{
                fillColor: '#BABABA',
              }}
              width={32}
              height={32}
            />
            <Paragraph>My location</Paragraph>
          </NavList.Item>
          {hasSubscriptionFeaturesEnabled && portalUrl && (
            <NavList.Item chevron onClick={() => unsubscribeHandler(portalUrl)}>
              <ReactSvg
                toDisplay={settings()}
                options={{
                  fillColor: '#BABABA',
                }}
                width={32}
                height={32}
              />
              <Paragraph>Manage subscription</Paragraph>
            </NavList.Item>
          )}
          <NavList.Item
            chevron
            onClick={() => history.push('/maps/settings/account/delete')}
          >
            <ReactSvg
              toDisplay={cross()}
              options={{
                fillColor: '#BABABA',
              }}
              width={32}
              height={32}
            />
            <Paragraph>Delete account</Paragraph>
          </NavList.Item>
          {deviceID && (
            <NavList.Item
              onClick={() => {
                copyTextToClipboard({
                  text: deviceID,
                  setCopied: setTextCopied,
                });
              }}
            >
              <ReactSvg
                toDisplay={settings()}
                options={{
                  fillColor: '#BABABA',
                }}
                width={32}
                height={32}
              />
              <Box p={2} width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>Device ID</Box>
                  {textCopied && (
                    <Box fontSize={1} fontWeight="500" color="success">
                      Copied!
                    </Box>
                  )}
                </Box>
                <DeviceIdText fontSize={1}>{deviceID}</DeviceIdText>
              </Box>
            </NavList.Item>
          )}
          {GITHUB_SHA && (
            <NavList.Item
              onClick={() => {
                copyTextToClipboard({
                  text: GITHUB_SHA,
                  setCopied: setShaCopied,
                });
              }}
            >
              <ReactSvg
                toDisplay={settings()}
                options={{
                  fillColor: '#BABABA',
                }}
                width={32}
                height={32}
              />
              <Box p={2} width="100%">
                <Box
                  display="flex"
                  justifyContent="space-between"
                  alignItems="center"
                >
                  <Box>Build version</Box>
                  {shaCopied && (
                    <Box fontSize={1} fontWeight="500" color="success">
                      Copied!
                    </Box>
                  )}
                </Box>
                <DeviceIdText fontSize={1}>{GITHUB_SHA}</DeviceIdText>
              </Box>
            </NavList.Item>
          )}
        </NavList>
      </LayoutBody>
    </BasicDrawerLayout>
  );
};

export default AccountSettingsPage;
